"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPricePerGJ = exports.canCreateCalculation = exports.getCalculationValidationErrors = exports.calculateSurplusResults = exports.calculateResults = exports.getYearValues = exports.getYearMultipliers = exports.MODEL_LAST_YEAR = exports.getComparisonFuelParam = exports.getInitialSecondComparisonFuelParam = exports.getInitialComparisonFuelParam = exports.getOriginFuelParam = exports.getInitialSecondFossilFuelParam = exports.getInitialFossilFuelParam = void 0;
const lodash_1 = require("lodash");
const getInitialFossilFuelParam = (metadata) => (0, exports.getOriginFuelParam)(1, 1, metadata);
exports.getInitialFossilFuelParam = getInitialFossilFuelParam;
const getInitialSecondFossilFuelParam = (metadata) => (0, exports.getOriginFuelParam)(2, 1, metadata);
exports.getInitialSecondFossilFuelParam = getInitialSecondFossilFuelParam;
const getOriginFuelParam = (fuel, engine, metadata) => {
    return {
        fuel_type: fuel,
        engine_type: engine,
        fuel_price: metadata.fuels[fuel].price,
        foc: 100000,
        engine_efficiency: metadata["efficiency-fuel-engine"][fuel][engine],
    };
};
exports.getOriginFuelParam = getOriginFuelParam;
const getInitialComparisonFuelParam = (metadata) => (0, exports.getComparisonFuelParam)(23, 1, metadata);
exports.getInitialComparisonFuelParam = getInitialComparisonFuelParam;
const getInitialSecondComparisonFuelParam = (metadata) => (0, exports.getComparisonFuelParam)(21, 1, metadata);
exports.getInitialSecondComparisonFuelParam = getInitialSecondComparisonFuelParam;
const getComparisonFuelParam = (fuel, engine, metadata) => {
    return {
        engine_type: engine,
        fuel_type: fuel,
        fuel_price: metadata.fuels[fuel].price,
        engine_change_cost: metadata.fuels[fuel].default_engine_change_cost,
        engine_efficiency: metadata["efficiency-fuel-engine"][fuel][engine],
        intensity: metadata["intensity-fuel-engine"][fuel][engine],
    };
};
exports.getComparisonFuelParam = getComparisonFuelParam;
exports.MODEL_LAST_YEAR = 2050;
const getYearMultipliers = (allYears) => {
    return allYears.reduce((result, y, i) => {
        let years = 0;
        if (y === exports.MODEL_LAST_YEAR) {
            years = 1;
        }
        else {
            years = allYears[i + 1] - y;
        }
        return {
            ...result,
            [y]: years,
        };
    }, {});
};
exports.getYearMultipliers = getYearMultipliers;
const calculateComparisonData = (comparison, mixYears, yearMultipliers) => {
    const mixPath = mixYears
        .map((year) => {
        const mixYear = comparison.mixes.find((yearData) => yearData.year === year);
        if (!mixYear) {
            return undefined;
        }
        const fuel_cost = combineMixFuelItems("fuel_cost", mixYear);
        const emission_cost = combineMixFuelItems("emission_cost", mixYear);
        const annual_technology_cost_for_alternative_fuel_ships = mixYear.annual_technology_cost_for_alternative_fuel_ships;
        const co2_equivalent_emissions = combineMixFuelItems("co2_equivalent_emissions", mixYear);
        const penalty = mixYear.total_penalty_costs;
        return {
            year,
            fuel_cost,
            emission_cost,
            annual_technology_cost_for_alternative_fuel_ships,
            co2_equivalent_emissions,
            penalty,
            value: fuel_cost +
                emission_cost +
                annual_technology_cost_for_alternative_fuel_ships +
                penalty,
        };
    })
        .filter((d) => d);
    const mixCumulativeCost = mixPath.reduce((r, v) => r + (v?.value ?? 0) * yearMultipliers[v?.year], 0);
    const mixCumulativeEmissions = mixPath.reduce((r, v) => r + (v?.co2_equivalent_emissions ?? 0) * yearMultipliers[v?.year], 0);
    const mixesHavePenalties = (0, lodash_1.some)(mixPath, (y) => y !== undefined && y.penalty > 0);
    const compliance = mixPath.map((y) => ({
        year: y.year,
        is_compliant: y.penalty === 0,
    }));
    const fuelMixMaxCost = Math.max(...mixPath.map((y) => Math.max(y?.emission_cost ?? 0, y?.penalty ?? 0, y?.fuel_cost ?? 0, y?.annual_technology_cost_for_alternative_fuel_ships ?? 0)));
    return {
        mixPath,
        mixCumulativeCost,
        mixCumulativeEmissions,
        mixesHavePenalties,
        compliance,
        fuelMixMaxCost,
    };
};
const combineMixFuelItems = (key, mixYear) => {
    const original = mixYear.original_fuel[key];
    const alternative = mixYear.alternative_fuel[key];
    if ((0, lodash_1.isNumber)(original) && (0, lodash_1.isNumber)(alternative)) {
        return original + alternative;
    }
    return 0;
};
const getYearValues = (mixYears, key) => mixYears.map((mixYear) => ({
    year: mixYear.year,
    value: mixYear[key],
}));
exports.getYearValues = getYearValues;
const calculateResults = (params, fuelTypes, calculation) => {
    const originalFuel = calculation.original_fuel;
    const alternativeFuels = params.comparison_fuels.map((cf) => fuelTypes[cf.fuel_type]);
    const mixYears = (0, lodash_1.chain)(calculation.comparisons)
        .flatMap((c) => c.mixes.map((year) => year.year))
        .uniq()
        .sort()
        .value();
    const lastYear = Math.max(...mixYears);
    const lastAccumulationYear = lastYear === exports.MODEL_LAST_YEAR ? exports.MODEL_LAST_YEAR : lastYear + 4;
    const yearMultipliers = (0, exports.getYearMultipliers)(mixYears);
    const originalPath = mixYears.map((year) => {
        const originalFuelYearDatum = originalFuel.timeline.find((p) => p.year === year);
        const penalty = originalFuelYearDatum?.penalty ?? 0;
        const emission_cost = originalFuelYearDatum?.emission_cost ?? 0;
        return {
            year,
            value: emission_cost + penalty + originalFuel.fuel_cost,
            emission_cost,
            penalty,
            co2_equivalent_emissions: calculation.co2_equivalent_emissions,
            fuel_cost: originalFuel.fuel_cost,
        };
    });
    const originalCumulativeCost = originalPath.reduce((r, v) => r + v.value * yearMultipliers[v.year], 0);
    const originalCumulativeEmissions = originalPath.reduce((r, v) => r + v.co2_equivalent_emissions * yearMultipliers[v.year], 0);
    const comparisonCalculations = calculation?.comparisons.map((comparison) => calculateComparisonData(comparison, mixYears, yearMultipliers));
    const originalMaxCost = Math.max(...originalPath.map((y) => Math.max(y.emission_cost, y.fuel_cost, y.penalty)));
    const maxCost = Math.max(originalMaxCost, ...comparisonCalculations.map((c) => c.fuelMixMaxCost));
    const complianceData = {
        comparisons: comparisonCalculations.map((c) => c.compliance),
        combined: mixYears.map((year) => ({
            year,
            is_compliant: comparisonCalculations.every((calculation) => calculation.compliance.find((cy) => cy.year === year)?.is_compliant),
        })),
    };
    return {
        originalFuel,
        alternativeFuels,
        cumulativeCosts: {
            original: originalCumulativeCost,
            fuelMixes: comparisonCalculations.map((c) => c.mixCumulativeCost),
        },
        cumulativeEmissions: {
            original: originalCumulativeEmissions,
            fuelMixes: comparisonCalculations.map((c) => c.mixCumulativeEmissions),
        },
        originalPathTimeline: originalPath,
        fuelMixTimelines: comparisonCalculations.map((c) => c.mixPath),
        lastAccumulationYear,
        complianceData,
        maxCost,
    };
};
exports.calculateResults = calculateResults;
const calculateSurplusResults = (calculation) => {
    const originalFuel = calculation.original_fuel;
    const years = (0, lodash_1.chain)(calculation.co2_surplus)
        .flatMap((c) => c.year)
        .uniq()
        .sort()
        .value();
    const lastYear = Math.max(...years);
    const lastAccumulationYear = lastYear === exports.MODEL_LAST_YEAR ? exports.MODEL_LAST_YEAR : lastYear + 4;
    const yearMultipliers = (0, exports.getYearMultipliers)(years);
    const originalPath = years.map((year) => {
        const yearData = originalFuel.timeline.find((p) => p.year === year);
        const penalty = yearData?.penalty ?? 0;
        const emission_cost = yearData?.emission_cost ?? 0;
        return {
            year,
            value: emission_cost + penalty + originalFuel.fuel_cost,
            emission_cost,
            penalty,
            co2_equivalent_emissions: calculation.co2_equivalent_emissions,
            fuel_cost: originalFuel.fuel_cost,
        };
    });
    const originalCumulativeCost = originalPath.reduce((r, v) => r + v.value * yearMultipliers[v.year], 0);
    const originalCumulativeEmissions = originalPath.reduce((r, v) => r + v.co2_equivalent_emissions * yearMultipliers[v.year], 0);
    const maxCost = Math.max(...originalPath.map((y) => Math.max(y.emission_cost, y.fuel_cost, y.penalty)));
    const complianceData = {
        combined: calculation.penalty.map((yearValue) => ({
            year: yearValue.year,
            is_compliant: yearValue.value === 0,
        })),
    };
    return {
        originalFuel,
        cumulativeCosts: {
            original: originalCumulativeCost,
        },
        cumulativeEmissions: {
            original: originalCumulativeEmissions,
        },
        originalPathTimeline: originalPath,
        lastAccumulationYear,
        complianceData,
        maxCost,
    };
};
exports.calculateSurplusResults = calculateSurplusResults;
const getCalculationValidationErrors = (params, metadata) => {
    const errors = [];
    // Check for required null fields
    if (params.fuels.length === 0) {
        errors.push("At least one fuel source is required");
    }
    else {
        const originalFuel = params.fuels[0];
        if (originalFuel.fuel_price === null ||
            originalFuel.fuel_price === undefined) {
            errors.push("Fossil fuel price is required");
        }
        if (originalFuel.engine_efficiency === null ||
            originalFuel.engine_efficiency === undefined) {
            errors.push("Fossil fuel engine efficiency is required");
        }
        if ("foc" in originalFuel &&
            (originalFuel.foc === null || originalFuel.foc === undefined)) {
            errors.push("Fuel oil consumption is required for fleet calculator");
        }
    }
    // Check comparison fuels
    if (!params.comparison_fuels || params.comparison_fuels.length === 0) {
        errors.push("At least one comparison fuel is required");
    }
    else {
        const compareFuel = params.comparison_fuels[0];
        if (compareFuel.fuel_price === null ||
            compareFuel.fuel_price === undefined) {
            errors.push("Comparison fuel price is required");
        }
        if (compareFuel.engine_efficiency === null ||
            compareFuel.engine_efficiency === undefined) {
            errors.push("Comparison fuel engine efficiency is required");
        }
        if (compareFuel.intensity === null || compareFuel.intensity === undefined) {
            errors.push("Comparison fuel intensity is required");
        }
        if (compareFuel.engine_change_cost === null ||
            compareFuel.engine_change_cost === undefined) {
            errors.push("Comparison fuel engine change cost is required");
        }
    }
    // Check voyage-specific fields
    if ("imo_number" in params) {
        if (params.imo_number === null || params.imo_number === undefined) {
            errors.push("IMO number is required");
        }
    }
    if ("draft" in params) {
        if (params.draft === null || params.draft === undefined) {
            errors.push("Draft is required");
        }
    }
    if ("speed" in params) {
        if (params.speed === null || params.speed === undefined) {
            errors.push("Speed is required");
        }
    }
    // Check fleet calculator fields
    if ("fleet_size" in params) {
        if (params.fleet_size === null || params.fleet_size === undefined) {
            errors.push("Fleet size is required");
        }
    }
    return errors;
};
exports.getCalculationValidationErrors = getCalculationValidationErrors;
const canCreateCalculation = (params, metadata) => {
    const errors = (0, exports.getCalculationValidationErrors)(params, metadata);
    if (errors.length > 0) {
        return false;
    }
    // Check intensity comparison
    const originalIntensity = metadata["intensity-fuel-engine"][params.fuels[0].fuel_type][params.fuels[0].engine_type];
    const compareIntensity = params.comparison_fuels[0].intensity ??
        metadata["intensity-fuel-engine"][params.comparison_fuels[0].fuel_type][params.comparison_fuels[0].engine_type];
    return originalIntensity > compareIntensity;
};
exports.canCreateCalculation = canCreateCalculation;
const getPricePerGJ = (pricePerTn, lcv) => {
    // pricePerTon = €/tn
    // lcv = MJ/g
    const MJPerTn = lcv * 1000000;
    const GJPerTn = MJPerTn / 1000;
    const tnPerGJ = 1 / GJPerTn;
    const pricePerGJ = pricePerTn * tnPerGJ;
    return pricePerGJ;
};
exports.getPricePerGJ = getPricePerGJ;
